import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { brandDisplayLabel } from "../lib/counterCultureBrand";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";

/**
 * OutletContext (Phase 181.39 · Multi-Outlet Isolation)
 * ----------------------------------------------------
 * Server-authoritative outlet context for the entire React tree.
 *
 * Guarantees:
 *   - The active outlet is loaded from `/outlets/current` (server pin) once
 *     the user is authenticated, not from any client state.
 *   - Switching outlets calls `/outlets/set-active` and, on success,
 *     invalidates every React Query cache so stale per-outlet data is
 *     purged before any component reads it again.
 *   - A global `outlet:changed` event fires so legacy pages (using plain
 *     axios + useEffect) can also refetch on switch.
 *   - `activeOutletId` is exposed as a stable string that page components
 *     can drop straight into their queryKey (e.g. ['orders', outletId]).
 */
const OutletContext = createContext(null);

export function OutletProvider({ children }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [current, setCurrent] = useState(null);     // { id, name, code, is_primary, ... }
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  const isOwner = ["owner", "admin", "user", "manager", "accountant"].includes(user?.role || "");

  const loadCurrent = useCallback(async () => {
    try {
      const r = await api.get("/outlets/current");
      setCurrent(r.data);
    } catch {
      setCurrent(null);
    }
  }, []);

  const loadOutlets = useCallback(async () => {
    if (!isOwner) return;
    try {
      const r = await api.get("/outlets?active_only=true");
      setOutlets(r.data || []);
    } catch {
      setOutlets([]);
    }
  }, [isOwner]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    Promise.all([loadCurrent(), loadOutlets()]).finally(() => setLoading(false));
  }, [user, loadCurrent, loadOutlets]);
  useEffect(()=>{
    const changed=()=>{setCurrent(null);setOutlets([]);qc.removeQueries({predicate:()=>true});loadCurrent();loadOutlets();};
    window.addEventListener('env:changed',changed);
    return()=>window.removeEventListener('env:changed',changed);
  },[qc,loadCurrent,loadOutlets]);

  // Aggressive cache purge on outlet switch — TanStack Query invalidates
  // every cached key so subsequent renders refetch under the new scope.
  const switchOutlet = useCallback(async (nextOutletId) => {
    if (!nextOutletId || nextOutletId === current?.id || switching) return { ok: false };
    setSwitching(true);
    try {
      await api.post("/outlets/set-active", { outlet_id: nextOutletId });
      // Refetch /outlets/current so `current` reflects the server-authoritative
      // pin even when the cached outlets[] list is stale or the target isn't
      // in it (e.g. a freshly-created outlet or a non-owner viewer).
      let next = null;
      try {
        const r = await api.get("/outlets/current");
        next = r.data;
        setCurrent(next);
      } catch {
        next = outlets.find((o) => o.id === nextOutletId) || null;
        setCurrent(next);
      }
      // Purge every cache — the safest way to guarantee no cross-outlet bleed.
      await qc.invalidateQueries();
      qc.removeQueries({ predicate: () => true });
      window.dispatchEvent(new CustomEvent("outlet:changed", { detail: { outlet: next } }));
      return { ok: true, outlet: next };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Switch failed" };
    } finally {
      setSwitching(false);
    }
  }, [current?.id, switching, outlets, qc]);

  const value = useMemo(() => ({
    activeOutletId: current?.id || "",
    activeOutletCode: current?.code || "",
    activeOutletName: brandDisplayLabel(current?.name || ""),
    isPrimary: !!current?.is_primary,
    current,
    outlets,
    loading,
    switching,
    canSwitch: isOwner,
    switchOutlet,
    refresh: loadCurrent,
  }), [current, outlets, loading, switching, isOwner, switchOutlet, loadCurrent]);

  return <OutletContext.Provider value={value}>{children}</OutletContext.Provider>;
}

export function useOutletContext() {
  const ctx = useContext(OutletContext);
  if (!ctx) {
    // Safe fallback for components mounted outside the provider (e.g. Login,
    // public order page).  Nothing to refetch there so the null-state is fine.
    return {
      activeOutletId: "", activeOutletCode: "", activeOutletName: "",
      isPrimary: false, current: null, outlets: [], loading: false,
      switching: false, canSwitch: false,
      switchOutlet: async () => ({ ok: false }),
      refresh: () => {},
    };
  }
  return ctx;
}
